import React, { useEffect, useState } from "react";
import {
  listBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost,
} from "../../services/blog";
import { uploadImageToR2 } from "../../services/r2";
import { Plus, Edit3, Trash2, X, Upload, Image as ImageIcon, Eye, EyeOff } from "lucide-react";

const LANGS = ["az", "en", "ru"];

function emptyMl() { return { az: "", en: "", ru: "" }; }
function emptyPost() {
  return {
    slug: "",
    cover_url: "",
    date: new Date().toISOString().slice(0, 10),
    read_min: 4,
    eyebrow: emptyMl(),
    title:   emptyMl(),
    excerpt: emptyMl(),
    body:    emptyMl(),
    published: true,
  };
}

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("az");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    setLoading(true);
    const posts = await listBlogPosts({ onlyPublished: false });
    setItems(posts);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const open = (it) => {
    if (it) {
      setEditing({
        ...emptyPost(),
        ...it,
        eyebrow: { ...emptyMl(), ...(it.eyebrow || {}) },
        title:   { ...emptyMl(), ...(it.title   || {}) },
        excerpt: { ...emptyMl(), ...(it.excerpt || {}) },
        body:    { ...emptyMl(), ...(it.body    || {}) },
      });
    } else {
      setEditing(emptyPost());
    }
  };
  const close = () => setEditing(null);

  const setMl = (field, value) => {
    setEditing((s) => ({ ...s, [field]: { ...s[field], [tab]: value } }));
  };

  const onPickCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const url = await uploadImageToR2(f, { folder: "blog" });
      setEditing((s) => ({ ...s, cover_url: url }));
    } catch (ex) { alert(ex.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!editing.title?.az && !editing.title?.en) {
      return alert("Ən azı bir dildə başlıq yazın.");
    }
    setBusy(true);
    try {
      const payload = {
        slug: editing.slug || "",
        cover_url: editing.cover_url || "",
        date: editing.date || new Date().toISOString().slice(0, 10),
        read_min: Number(editing.read_min) || 4,
        eyebrow: editing.eyebrow,
        title:   editing.title,
        excerpt: editing.excerpt,
        body:    editing.body,
        published: editing.published !== false,
      };
      if (editing.id) await updateBlogPost(editing.id, payload);
      else await createBlogPost(payload);
      await reload();
      close();
    } catch (e) {
      alert(e.message || "Xəta baş verdi.");
    } finally { setBusy(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`'${it.title?.az || it.title?.en || "yazı"}' silinsin?`)) return;
    await deleteBlogPost(it.id);
    reload();
  };

  return (
    <div className="p-10" data-testid="admin-blog">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Sistem</div>
          <h1 className="font-display text-5xl mt-1">Yeniliklər</h1>
          <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] mt-2">
            {items.length} yazı
          </div>
        </div>
        <button
          onClick={() => open(null)}
          className="btn-primary"
          style={{ textTransform: "none", letterSpacing: "0" }}
          data-testid="add-blog"
        >
          <Plus size={14}/> Yeni yazı
        </button>
      </div>

      <div className="border border-[var(--border)]">
        <div className="grid grid-cols-12 text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)]">
          <div className="col-span-1">Şəkil</div>
          <div className="col-span-5">Başlıq</div>
          <div className="col-span-2">Üst yazı</div>
          <div className="col-span-2">Tarix</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Əməl</div>
        </div>

        {loading && (
          <div className="p-10 text-center text-[var(--text-muted)]">Yüklənir…</div>
        )}

        {!loading && items.map((it) => (
          <div key={it.id} className="grid grid-cols-12 px-4 py-3 border-b border-[var(--border-soft)] items-center text-sm" data-testid={`blog-row-${it.id}`}>
            <div className="col-span-1">
              {it.cover_url
                ? <img src={it.cover_url} alt="" className="w-12 h-12 object-cover"/>
                : <div className="w-12 h-12 bg-[var(--surface)] flex items-center justify-center"><ImageIcon size={18} className="text-[var(--text-muted)]"/></div>}
            </div>
            <div className="col-span-5 font-display text-lg truncate">{it.title?.az || it.title?.en || "(başlıq yoxdur)"}</div>
            <div className="col-span-2 text-[var(--text-2)] truncate">{it.eyebrow?.az || it.eyebrow?.en || "—"}</div>
            <div className="col-span-2 font-mono text-xs">{it.date || "—"}</div>
            <div className="col-span-1 text-xs">
              {it.published === false
                ? <span className="inline-flex items-center gap-1 text-[var(--text-muted)]"><EyeOff size={12}/> Gizli</span>
                : <span className="inline-flex items-center gap-1 text-[var(--primary)]"><Eye size={12}/> Yayımda</span>}
            </div>
            <div className="col-span-1 flex justify-end gap-2">
              <button onClick={() => open(it)} className="p-1 hover:text-[var(--primary)]" data-testid={`edit-blog-${it.id}`}><Edit3 size={14}/></button>
              <button onClick={() => del(it)} className="p-1 hover:text-[var(--accent)]" data-testid={`del-blog-${it.id}`}><Trash2 size={14}/></button>
            </div>
          </div>
        ))}

        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-[var(--text-muted)]">Hələ yazı yoxdur. Yuxarıdan ilk yazını əlavə edin.</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-6 overflow-y-auto" onClick={close}>
          <div className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-3xl p-8" onClick={(e) => e.stopPropagation()} data-testid="blog-modal">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-3xl">{editing.id ? "Düzəliş" : "Yeni"} Yazı</h2>
              <button onClick={close}><X/></button>
            </div>

            {/* Lang tabs */}
            <div className="flex gap-1 mb-6">
              {LANGS.map((l) => (
                <button
                  key={l}
                  onClick={() => setTab(l)}
                  className={`px-4 py-2 text-xs font-medium border-b-2 ${tab === l ? "border-[var(--primary)] text-[var(--primary)]" : "border-transparent text-[var(--text-2)]"}`}
                  data-testid={`blog-lang-${l}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Üst yazı <span className="font-normal normal-case">— məs: BƏLƏDÇİ, YERLİ SƏHNƏ</span></label>
                <input
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                  value={editing.eyebrow?.[tab] || ""}
                  onChange={(e) => setMl("eyebrow", e.target.value)}
                  data-testid="blog-f-eyebrow"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Başlıq</label>
                <input
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-display text-lg"
                  value={editing.title?.[tab] || ""}
                  onChange={(e) => setMl("title", e.target.value)}
                  data-testid="blog-f-title"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Qısa təsvir (excerpt) <span className="font-normal normal-case">— kart üzərində görünən mətn</span></label>
                <textarea
                  rows={3}
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                  value={editing.excerpt?.[tab] || ""}
                  onChange={(e) => setMl("excerpt", e.target.value)}
                  data-testid="blog-f-excerpt"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Mətn (body) <span className="font-normal normal-case">— yazının tam məzmunu (paraqrafları enter ilə ayırın)</span></label>
                <textarea
                  rows={10}
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 leading-relaxed"
                  value={editing.body?.[tab] || ""}
                  onChange={(e) => setMl("body", e.target.value)}
                  data-testid="blog-f-body"
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Tarix</label>
                <input
                  type="date"
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm"
                  value={editing.date || ""}
                  onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                  data-testid="blog-f-date"
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Oxuma müddəti (dəq)</label>
                <input
                  type="number"
                  min={1}
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                  value={editing.read_min}
                  onChange={(e) => setEditing({ ...editing, read_min: e.target.value })}
                  data-testid="blog-f-read-min"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Slug <span className="font-normal normal-case">— URL-də görünür. Boş buraxılsa başlıqdan avtomatik yaranır.</span></label>
                <input
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm"
                  placeholder="180g-pres-nedir"
                  value={editing.slug || ""}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                  data-testid="blog-f-slug"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Üz şəkli</label>
                <div className="mt-1 flex items-center gap-3">
                  {editing.cover_url
                    ? <img src={editing.cover_url} alt="" className="w-24 h-24 object-cover border border-[var(--border)]"/>
                    : <div className="w-24 h-24 bg-[var(--surface)] flex items-center justify-center"><ImageIcon size={20} className="text-[var(--text-muted)]"/></div>}
                  <label className="btn-outline cursor-pointer" style={{ textTransform: "none", letterSpacing: "0" }}>
                    <Upload size={14}/> {uploading ? "Yüklənir…" : "Şəkil yüklə"}
                    <input type="file" accept="image/*" hidden onChange={onPickCover} data-testid="blog-f-cover-upload"/>
                  </label>
                  <input
                    className="flex-1 bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs"
                    placeholder="və ya şəkil URL-i"
                    value={editing.cover_url || ""}
                    onChange={(e) => setEditing({ ...editing, cover_url: e.target.value })}
                    data-testid="blog-f-cover-url"
                  />
                </div>
              </div>

              <div className="md:col-span-2 border border-[var(--border-soft)] p-3 bg-[var(--surface)]/40">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editing.published !== false}
                    onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                    data-testid="blog-f-published"
                  />
                  <span className="font-medium">Yayımda göstər</span>
                  <span className="text-[var(--text-muted)] text-xs font-normal">— işarəni götürsəniz, yazı saytda görünməz</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={close}
                className="btn-outline"
                style={{ textTransform: "none", letterSpacing: "0" }}
                data-testid="blog-cancel"
              >Ləğv et</button>
              <button
                onClick={save}
                disabled={busy}
                className="btn-primary"
                style={{ textTransform: "none", letterSpacing: "0" }}
                data-testid="blog-save"
              >{busy ? "…" : "Yadda saxla"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
